import * as Braze from '@braze/web-sdk'
import type { ContentCard } from '@braze/react-native-sdk'

/**
 * Defines the common properties shared across all Braze content card types
 * that need to be mapped to the ContentCard interface.
 */
type CommonCardProps = Pick<
  ContentCard,
  | 'id'
  | 'created'
  | 'expiresAt'
  | 'viewed'
  | 'clicked'
  | 'pinned'
  | 'dismissed'
  | 'dismissible'
  | 'isControl'
  | 'extras'
  | 'openURLInWebView'
>

function getBooleanProperty(card: Braze.Card, property: string): boolean {
  const value = Object.getOwnPropertyDescriptor(card, property)?.value
  return typeof value === 'boolean' ? value : false
}

/**
 * Extracts common properties from a Braze card object.
 * @param card The Braze card object.
 * @returns An object containing the common properties.
 */
function extractCommonCardProps(card: Braze.Card): CommonCardProps {
  return {
    id: card.id ?? '',
    created: card.updated ? card.updated.getTime() : 0,
    expiresAt: card.expiresAt ? card.expiresAt.getTime() : -1,
    viewed: card.viewed,
    // Default values for properties that might not exist on all Braze card types
    // but are required by ContentCard.
    clicked: getBooleanProperty(card, 'clicked'),
    pinned: getBooleanProperty(card, 'pinned'),
    dismissed: getBooleanProperty(card, 'dismissed'),
    dismissible: getBooleanProperty(card, 'dismissible'),
    isControl: getBooleanProperty(card, 'isControl'),
    extras: card.extras,
    openURLInWebView: false, // Assuming this is consistently false
  }
}

export function mapContentCardsToRN(card: Braze.Card): ContentCard {
  const commonProps = extractCommonCardProps(card)

  if (card instanceof Braze.ControlCard) {
    return {
      ...commonProps,
      type: 'Classic', // Control cards are mapped to 'Classic' type
      isControl: true, // Explicitly set for control cards
      title: '',
      cardDescription: '',
    } as const satisfies ContentCard
  }

  if (card instanceof Braze.ClassicCard) {
    return {
      ...commonProps,
      type: 'Classic',
      title: card.title,
      cardDescription: card.description,
      url: card.url,
      image: card.imageUrl ?? '',
    } as const satisfies ContentCard
  }

  if (card instanceof Braze.CaptionedImage) {
    return {
      ...commonProps,
      type: 'Captioned',
      image: card.imageUrl ?? '',
      imageAspectRatio: card.aspectRatio ?? -1,
      title: card.title,
      cardDescription: card.description,
      url: card.url,
    } as const satisfies ContentCard
  }

  if (card instanceof Braze.ImageOnly) {
    return {
      ...commonProps,
      type: 'ImageOnly',
      image: card.imageUrl ?? '',
      imageAspectRatio: card.aspectRatio ?? -1,
      url: card.url,
    } as const satisfies ContentCard
  }

  throw new Error('Cannot map Braze Web SDK card type to React Native SDK card type', {
    cause: {
      card,
    },
  })
}
